import * as React from 'react';
import groupBy from 'lodash/groupBy';
import { AutoComplete, type AutoCompleteProps } from 'antd';
import debounce from 'lodash/debounce';
import { useQuery } from '@tanstack/react-query';
import * as Store from '../store';
import SelectorEmpty from './SelectorEmpty';
import { generateFilterOptions, useRecentFilterOptions } from './utils';
import * as Styled from './styled';

export interface SavedFilterSelectorProps extends AutoCompleteProps {}

const SavedFilterSelector = (props: SavedFilterSelectorProps) => {
  const [search, setSearch] = React.useState<string>();
  const recentFilter = useRecentFilterOptions();
  const onSavedFilterSearch = Store.useOnSavedFilterSearch();
  const searchEnabled = (search?.length ?? 0) > 2;
  const searchedSavedFilters = useQuery(
    ['filter-dialog', 'filters', 'saved', search],
    () => onSavedFilterSearch?.(search),
    {
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      enabled: searchEnabled,
    },
  );
  const options = React.useMemo(
    () => {
      if (!searchEnabled) return recentFilter.data;

      return generateFilterOptions(
        Object.entries(groupBy(searchedSavedFilters.data, 'matchSource')).map(([key, value]) => ({
          label: key,
          options: value,
        })),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentFilter.data, searchedSavedFilters.data],
  );
  const handleSearch = React.useMemo(() => debounce(setSearch, 500), []);

  return (
    <AutoComplete
      {...props}
      autoFocus
      defaultOpen
      placeholder="Search by Name, Rules, ID or Creator"
      options={options}
      notFoundContent={<SelectorEmpty />}
      popupRender={(menu) => {
        if ((searchEnabled && searchedSavedFilters.isLoading) || recentFilter.isLoading) {
          return <Styled.DropdownSpin />;
        }

        if (searchEnabled) return menu;

        return (
          <Styled.SavedFilterSelectorDropdownContainer>
            {menu}
          </Styled.SavedFilterSelectorDropdownContainer>
        );
      }}
      onSearch={handleSearch}
    />
  );
};

export default React.memo(SavedFilterSelector);
